import React, {forwardRef, useCallback, useEffect, useReducer, useState} from 'react';
import {AutomationContext} from '../../context';
import {automationReducer, AutomationReducerType} from '../../reducer';
import {AutomationContextType, defaultContext} from '../../reducer/model';
import {BPMRulesView} from './view/BPMRules.view';
import {BPMFooter} from './view/BPMFooter';
import {StatusDialog} from './components/StatusDialog';
import {OldStatusesDialog} from './components/OldStatusesDialog/Dialog';
import {
    getCurSession,
    getInactiveStatusesTitles,
    setNewlyInsertedStatusFromReact,
    setSelectedStatusByStatusName
} from '../../../../_shared/GWTBridge/invoke/bpm/bpmMain';
import {bpmInsertStatus} from '../../../../services/bpmService';
import {BPMEvent, BPMEventAction} from './types';
import {useBPMAutomationContextAPI} from './hooks/useBPMAutomationContext';
import {getServerTexts as getBPMTexts} from './texts';
import {getServerTexts as getAutomationTexts} from '../../texts';
import {getServerTexts as getEmailServerTexts} from '../../../EmailEditor/texts';
import {getServerTexts as getAIServerTexts} from '../../../../Localization/AISearch/texts';
import {createPortal} from 'react-dom';
import {BPMHeaderToolbar} from './components/BPMHeaderToolbar';
import {useBPMHeaderPortalContainer} from './hooks/useBPMHeaderPortalContainer';

export const BPMDialogModule = forwardRef((props: {}, ref) => {
    const [isOpenStatusDialog, setIsOpenStatusDialog] = useState(false);
    const [isOpenStatusesListDialog, setIsOpenStatusesListDialog] = useState(false);
    const [inActiveStatusesTitles, setInActiveStatusesTitles] = useState<
        {value: string; label: string}[]
    >([]);
    const setTextReady = useState(false)[1];

    const [state, dispatch] = useReducer<AutomationReducerType, AutomationContextType>(
        automationReducer,
        {...defaultContext, useBridgeAPI: false},
        arg => arg
    );

    const portalHeaderContainer = useBPMHeaderPortalContainer();
    const {fromStatus, toStatus} = state;

    const contextValue = {...state, useBridgeAPI: false};
    const contextHandlers = useBPMAutomationContextAPI(dispatch, contextValue);

    const {openAutomationList, dispatchState} = contextHandlers;

    const closeStatusDialog = useCallback(() => {
        setIsOpenStatusDialog(false);
    }, []);

    const closeStatusesListDialog = useCallback(() => {
        setIsOpenStatusesListDialog(false);
        setInActiveStatusesTitles([]);
    }, []);

    const openStatusDialog = useCallback(() => {
        setIsOpenStatusDialog(true);
        setIsOpenStatusesListDialog(false);
    }, []);

    const openHiddenStatus = useCallback(() => {
        closeStatusDialog();
        setIsOpenStatusesListDialog(true);
    }, [closeStatusDialog]);

    const addNewStatus = useCallback(() => {
        const statuses = getInactiveStatusesTitles();

        dispatchState({toStatus: '', fromStatus: ''}); // Ensure that current status is cleared

        if (statuses.length > 0) {
            setInActiveStatusesTitles(
                statuses.map(status => ({
                    label: status.statusName,
                    value: status.statusName,
                    data: status
                }))
            );
        }

        openStatusDialog();
    }, [openStatusDialog, dispatchState]);

    const onStatusSave = useCallback(
        (toStatus: string) => {
            closeStatusDialog();
            dispatchState({toStatus});
            setSelectedStatusByStatusName(toStatus);
        },
        [closeStatusDialog, dispatchState]
    );

    const addSelectedStatus = useCallback(async (status: string) => {
        if (status) {
            setNewlyInsertedStatusFromReact(status);
            await bpmInsertStatus(status);
            setSelectedStatusByStatusName(status);
        }
    }, []);

    const showStatusSettings = useCallback(async () => {
        if (toStatus) {
            openStatusDialog();
        }
    }, [openStatusDialog, toStatus]);

    const onOpenRules = useCallback(
        async (fromStatus: string, toStatus: string) => {
            //open rules
            await openAutomationList({fromStatus, toStatus} as any);
        },
        [openAutomationList]
    );

    useEffect(() => {
        const handleBPMEvent: (e: any) => void = async (e: BPMEvent) => {
            const detail = e.detail;

            if (detail) {
                const {data} = detail;

                if (data) {
                    const {fromStatus, toStatus, action} = data;

                    if (action === BPMEventAction.openProperties) {
                        openStatusDialog();
                    } else if (action === BPMEventAction.openRules) {
                        await openAutomationList({fromStatus, toStatus} as any);
                    }

                    if (action === BPMEventAction.deselect) {
                        dispatchState({fromStatus: '', toStatus: ''});
                    } else if (fromStatus || toStatus) {
                        dispatchState({fromStatus, toStatus});
                    }
                }
            }
        };

        window.addEventListener('BpmChanges', handleBPMEvent);
        return () => window.removeEventListener('BpmChanges', handleBPMEvent);
    }, [openStatusDialog, openAutomationList]);

    useEffect(() => {
        Promise.all([
            getBPMTexts(),
            getAutomationTexts(),
            getEmailServerTexts(),
            getAIServerTexts()
        ]).then(() => {
            setTextReady(true);
        });
    }, []);

    return (
        <AutomationContext.Provider
            value={{
                ...contextValue,
                ...contextHandlers
            }}
        >
            {portalHeaderContainer
                ? createPortal(
                      <BPMHeaderToolbar key={getCurSession()} />,
                      document.getElementById('bpm-header-portal')
                  )
                : null}

            <BPMRulesView fromStatus={fromStatus} toStatus={toStatus} />

            {isOpenStatusesListDialog && (
                <OldStatusesDialog
                    onBack={openStatusDialog}
                    onClose={closeStatusesListDialog}
                    openSelectedStatus={addSelectedStatus}
                    options={inActiveStatusesTitles}
                />
            )}

            {isOpenStatusDialog && (
                <StatusDialog
                    onClose={closeStatusDialog}
                    onSave={onStatusSave}
                    currentStatus={toStatus}
                    showHiddenStatuses={
                        inActiveStatusesTitles.length > 0 ? openHiddenStatus : undefined
                    }
                />
            )}

            <BPMFooter
                addNewStatus={addNewStatus}
                showStatusSettings={showStatusSettings}
                toStatus={toStatus}
                fromStatus={fromStatus}
                onOpenRules={onOpenRules}
            />
        </AutomationContext.Provider>
    );
});
